'use client';

import dynamic from 'next/dynamic';
import { useCallback, useState, type CSSProperties } from 'react';

import { Media } from '@/components/primitives/Media';
import {
  CHORD_GRAPH_ASPECT_RATIO,
  CHORD_GRAPH_PLACEHOLDER_SRC,
  CHORD_GRAPH_PLACEHOLDER_WIDTH,
  CHORD_GRAPH_VIEWPORT_ROOT_MARGIN,
  CHORD_LABEL_BAND_PX,
} from '@/constants/chordGraph';
import { useInView } from '@/hooks/useInView';

import styles from './ChordGraphPlaceholder.module.css';

import type { ChordGraphProps } from '@/content/data/charts/chord';

const PixiChordGraphDynamic = dynamic(
  () => import('./PixiChordGraph').then((mod) => mod.PixiChordGraph),
  { ssr: false, loading: () => null },
);

/**
 * Client boundary for the below-fold chord chart. Keeps Introduction a
 * Server Component while code-splitting the Pixi/d3 bundle (`ssr: false`).
 *
 * Viewport gate (scroll audit 2026-07-15): defer the dynamic import until the
 * plate is near the viewport. Mount is sticky (`once: true`) — hero pin /
 * `clip-path` on `#introduction` flips IntersectionObserver and unmounting
 * the Application caused remount storms and WebGL teardown crashes.
 *
 * A static Show ALL PNG overlays the plate until the first mesh paint
 * (`onReady`), then unmounts — not restored on filter/resize remeshes.
 */
export function ChordGraphIsland(props: ChordGraphProps) {
  const { ref, inView } = useInView<HTMLDivElement>({
    once: true,
    margin: CHORD_GRAPH_VIEWPORT_ROOT_MARGIN,
  });
  const [isReady, setIsReady] = useState(false);
  const handleReady = useCallback(() => setIsReady(true), []);

  const showPlaceholder = !isReady;

  return (
    <div
      ref={ref}
      className={styles.island}
      style={
        {
          aspectRatio: String(CHORD_GRAPH_ASPECT_RATIO),
          '--chord-label-band': `${CHORD_LABEL_BAND_PX}px`,
        } as CSSProperties
      }
      role={showPlaceholder ? 'img' : undefined}
      aria-busy={showPlaceholder || undefined}
      aria-label={
        showPlaceholder
          ? inView
            ? 'Loading chord diagram'
            : props.caption
          : undefined
      }
    >
      {showPlaceholder ? (
        <div className={styles.placeholder} aria-hidden>
          <Media
            className={styles.placeholderImage}
            sources={[
              {
                src: CHORD_GRAPH_PLACEHOLDER_SRC,
                width: CHORD_GRAPH_PLACEHOLDER_WIDTH,
              },
            ]}
            sizes="(min-width: 64rem) 50vw, 100vw"
            alt=""
            aspectRatio="1"
            loading={inView ? 'eager' : 'lazy'}
            decoding="async"
          />
        </div>
      ) : null}

      {inView ? (
        <div className={styles.chart}>
          <PixiChordGraphDynamic {...props} onReady={handleReady} />
        </div>
      ) : null}
    </div>
  );
}
